import { convertMillisecondsToHHMM } from "../../util/Util";
import {
  getPicklistLocation,
  getPicklistStageTime,
  getRouteStatusMetrics,
  renderRouteSpeed,
} from "./Util";
import { STAGE_LOCATION_TYPE } from "../../outboundAmzl/util/Constants";
import { STATUS_TO_METRIC_KEY_MAP } from "../utils/EntityStatusManager";
import { DELIVERY_SPEED_FILTER } from "./Constants";

export const OVERFLOW_FILTER_TAG_LABEL = "With Overflow";

/**
 * Returns the filter tag label based on filter type and value.
 * For stageLocationType filters, returns a predefined overflow label.
 *
 * @param {string} filter - The filter type (e.g., 'stageLocationType')
 * @param {string} value - The filter value
 * @returns {string} The filter tag label to display
 */
export const getFilterTagLabel = (filter, value) => {
  if (filter === "stageLocationType") {
    return OVERFLOW_FILTER_TAG_LABEL;
  }
  if (filter === DELIVERY_SPEED_FILTER) {
    return renderRouteSpeed(value);
  }
  return value;
};

/**
 * Applies global stage location type filter to check for overflow staging locations.
 * Returns true if the route has any staging locations of type OVERFLOW.
 *
 * @param {Object} route - The route object to filter
 * @param {Array} route.stagingLocations - Array of staging location objects
 * @param {string} route.stagingLocations[].type - Type of staging location
 * @param {string} value - The filter value (returns true if empty)
 * @returns {boolean} True if route matches filter criteria or filter is empty
 */
export const applyGlobalStageLocationTypeFilter = (route, value) => {
  if (value.length === 0) return true;

  if (route.stagingLocations && Array.isArray(route.stagingLocations)) {
    return route.stagingLocations.some((location) => {
      return location.type === STAGE_LOCATION_TYPE.OVERFLOW;
    });
  }

  return false;
};

/**
 * Applies inline staging location filter by checking picklist locations.
 * Searches through route picklists to find matching staging locations.
 *
 * @param {Object} route - The route object to filter
 * @param {Array} route.picklists - Array of picklist objects
 * @param {Array} route.picklists[].carts - Array of cart objects in the picklist
 * @param {string} value - The location value to search for (case-insensitive)
 * @returns {boolean} True if any picklist location contains the search value or filter is empty
 */
export const applyInlineStagingLocationFilter = (route, value) => {
  if (value.length === 0) return true;

  if (route.picklists && Array.isArray(route.picklists)) {
    return route.picklists.some((picklist) => {
      if (picklist.carts && picklist.carts[0]) {
        const stagingLocation = getPicklistLocation(picklist);
        return (
          stagingLocation &&
          stagingLocation.toLowerCase().includes(value.toLowerCase())
        );
      }
      return false;
    });
  }

  return false;
};

/**
 * Applies inline stage time filter by checking route and picklist stage times.
 * Searches both route-level and picklist-level stage times for matches.
 *
 * @param {Object} route - The route object to filter
 * @param {number} [route.stageTime] - Route-level stage time in milliseconds
 * @param {Array} [route.picklists] - Array of picklist objects
 * @param {string} value - The time value to search for (case-insensitive, e.g., "01:30")
 * @returns {boolean} True if any stage time contains the search value or filter is empty
 */
export const applyInlineStageTimeFilter = (route, value) => {
  if (value.length === 0) return true;

  // Check route-level stage time
  if (route.stageTime) {
    const routeStageTime = convertMillisecondsToHHMM(route.stageTime);
    if (
      routeStageTime &&
      routeStageTime.toLowerCase().includes(value.toLowerCase())
    ) {
      return true;
    }
  }

  // Check picklist-level stage times
  if (route.picklists && Array.isArray(route.picklists)) {
    return route.picklists.some((picklist) => {
      const picklistStageTime = getPicklistStageTime(picklist);
      return (
        picklistStageTime &&
        picklistStageTime.toLowerCase().includes(value.toLowerCase())
      );
    });
  }

  return false;
};

/**
 * Handles inline filters
 * and general property matching for other filter types.
 *
 * @param {Object} route - The route object to filter
 * @param {string} filter - The filter type (e.g., 'status', 'driverType')
 * @param {string} key - The route property key to check for non-status filters
 * @param {Object} viewModelF - The view model filter object
 * @param {Array} viewModelF[filter] - Array of filter values for the specified filter type
 * @param {string} [viewModelF.productType] - Product type for status filter processing
 * @returns {boolean} True if route matches any filter value or filter is empty
 */
export const filterOption = (route, filter, key, viewModelF) => {
  if (!viewModelF[filter] || viewModelF[filter].length === 0) {
    return true;
  }

  return viewModelF[filter].some((filterValue) => {
    if (filter === "status") {
      const metrics = getRouteStatusMetrics(route, viewModelF.productType);
      const metricKey = STATUS_TO_METRIC_KEY_MAP[filterValue];
      return metricKey && metrics[metricKey];
    }
    return (
      route[key] && route[key].includes && route[key].includes(filterValue)
    );
  });
};



// WEBPACK FOOTER //
// ./src/components/RouteStatus/utils/FilterUtil.js